// Plain-English "what to do" and error explanations (prototype todo() + ERR copy, adapted to v1:
// numbers are fixed in the sheet, sending happens from the CLI).
import type { Run, WaError } from "@/data/types";
import type { CapUsage } from "./cap";
import { isWaiting } from "./overview";
import type { Person } from "./people";
import { RUN_STALE_MS } from "./runs";

export type Todo = { act: boolean; text: string };

export function todo(p: Person, planned: number | null): Todo {
  if (isWaiting(p)) return { act: true, text: "Reply to them" };
  switch (p.status) {
    case "invalid":
      return { act: true, text: "Fix the number in the sheet" };
    case "failed":
      return { act: true, text: "Check the number" };
    case "retrying":
      return { act: false, text: "Nothing · the next send retries it" };
    case "over_limit":
      return { act: true, text: "Retry from Overview when the limit has room" };
    case "opted_out":
      return { act: false, text: "Nothing · they said stop" };
    case "queued":
      return { act: false, text: planned ? `Nothing · goes in batch ${planned}` : "Nothing · goes in a later batch" };
    case "sending":
    case "sent":
      return { act: false, text: "Nothing · on the way" };
    case "not_on_list":
      return { act: false, text: "Nothing · not on the list" };
    default:
      return { act: false, text: "Nothing · they have it" };
  }
}

const KNOWN: Record<string, { title: string; detail: string }> = {
  "131026": {
    title: "Not on WhatsApp — check the number",
    detail:
      "WhatsApp could not deliver it. The number may be wrong, or the person does not use WhatsApp. Fix it in the sheet, or call them.",
  },
  "131049": {
    title: "WhatsApp held the message back",
    detail: "Meta limits how many business messages one person gets. It will not be retried; call them if it matters.",
  },
  "131048": {
    title: "Over WhatsApp's 24-hour limit",
    detail:
      "Meta refused it because our 24-hour limit was full. It is not retried on its own: press Retry all on Overview and the sender sends it first in its next run, if the limit has room.",
  },
  "131047": {
    title: "More than 24 hours since they last wrote",
    detail: "Only an approved template can be sent now.",
  },
  "131051": { title: "Message type not supported", detail: "WhatsApp rejected the message format." },
  "131021": { title: "Sender and recipient are the same number", detail: "Fix the number in the sheet." },
  "131000": {
    title: "WhatsApp had a hiccup",
    detail: "Something went wrong on Meta’s side. The next send run tries again on its own.",
  },
  invalid_phone: {
    title: "Phone number is not a valid mobile",
    detail: "It is not a 10-digit Indian mobile. Fix it in the sheet, run `wa publish`, and they join the next batch.",
  },
  STOP: {
    title: "Asked us to stop",
    detail: "They replied STOP. WhatsApp rules: we can’t message them again unless they write to us first.",
  },
};

export interface Explained {
  title: string;
  detail: string;
  /** footer line under the error */
  code: string;
  retrying: boolean;
}

export function explainError(p: Person, err: WaError | null): Explained | null {
  if (p.status === "opted_out") return { ...KNOWN.STOP!, code: "Opt-out on WhatsApp", retrying: false };
  if (!err) return null;
  const k = err.code == null ? "" : String(err.code);
  const known = KNOWN[k];
  const isNum = /^\d+$/.test(k);
  const retrying = p.status === "retrying";
  const detail =
    (known?.detail ?? err.detail) ||
    (retrying ? "Nothing reached WhatsApp. The next send run tries again on its own." : "");
  const raw = err.title && known ? ` · ${err.title}` : "";
  return {
    title: known?.title ?? (err.title || "Send failed"),
    detail:
      p.status === "invalid" && p.primary?.recipient?.phoneError
        ? `${detail} (${p.primary.recipient.phoneError})`
        : detail,
    code: isNum ? `WhatsApp error ${k}${raw}` : k === "invalid_phone" ? "Caught before sending" : k || "No error code",
    retrying,
  };
}

// ---------------------------------------------------------------- sender strip

export type SenderState = "running" | "capped" | "ready" | "done" | "empty";
export const SENDER_BADGE: Record<SenderState, { cls: string; label: string }> = {
  running: { cls: "running", label: "Sending" },
  capped: { cls: "capped", label: "Waiting for cap" },
  ready: { cls: "ready", label: "Ready" },
  done: { cls: "off", label: "All sent" },
  empty: { cls: "off", label: "No list yet" },
};

export function senderState(runs: Run[], cap: CapUsage, waiting: number, now: number, onList = 1): SenderState {
  if (runs.some((r) => r.finishedAt == null && r.startedAt != null && now - r.startedAt < RUN_STALE_MS)) {
    return "running";
  }
  if (!onList) return "empty";
  if (!waiting) return "done";
  return cap.left === 0 ? "capped" : "ready";
}
